/**
 * Everything on the maintenance page that differs between the ten apps.
 *
 * Ported from alidrop-app's src/components/maintenance/content.ts. This app is
 * config profile B (Figma node 147:2003) — several values differ from the
 * AliDrop reference and must NOT be copied across:
 *
 *  - Font weights are a different set from AliDrop's (Profile A): eyebrow 700
 *    not 500, reassurance 400 not 300, promoName/promoHeadline 500 not 600,
 *    promoLink 700 not 600.
 *  - The status-pill border is 14% opacity, not AliDrop's 27%.
 *  - The background rings sit 890/834 of 1440 (61.8%/57.9%), not AliDrop's
 *    781/725 (54.2%/50.3%) — this app and Spocket place them 109px further
 *    right than the other eight apps.
 *  - The glow is specified as an explicit stop colour + opacity (not derived
 *    from the accent at a fixed 10%): 1728x808 at 82%, opacity 1.0, stop
 *    rgba(230,231,255,1). See index.tsx for how this is consumed.
 *  - This is the ONLY app that promotes AliDrop rather than DropshipTool
 *    itself.
 *
 * Values live in plans/maintenance-pages-all-apps/src/tokens.json
 * (apps.dropshiptool), cross-checked against the Figma frame in the task spec.
 */

export type Promo = {
  name: string;
  headline: string;
  blurb: string;
  linkLabel: string;
  linkUrl: string;
};

/** This app's only promo target. Values verbatim from the verified Figma copy. */
export const ALIDROP: Promo = {
  name: "AliDrop",
  headline: "Automate your AliExpress dropshipping journey with AliDrop",
  blurb:
    "Easily source the best AliExpress products, along with top selections from Alibaba and Temu, and trusted US and EU suppliers.",
  linkLabel: "Alidrop.co",
  linkUrl:
    "https://app.alidrop.co/register?utm_source=dropshiptool&utm_medium=maintenance",
};

/** Identical on every frame — kept here so the ten copies can't drift. */
export const SHARED = {
  status: "Maintenance in progress",
  statusShort: "Maintenance",
  body: "We're running scheduled work on the platform. Nothing is lost, and your store keeps selling. We'll be back shortly.",
  cta: "Back to Home",
  reassurance: "This page refreshes itself when we're live.",
  promoEyebrow: "While you wait",
};

export const APP = {
  name: "Dropshiptool",
  /** `${name} will be back soon.` — the only per-app string in the headline. */
  home: "/",
  promo: ALIDROP,

  /** Headline ink. Also the source for the divider and card border. */
  ink: "#0E0033",
  /** Body, reassurance, eyebrow, blurb, pill label. */
  muted: "#717680",

  /**
   * Logo box, measured from this app's own Figma frame (147:2003). Rendered
   * with object-fit: contain, so a wrong value letterboxes rather than
   * distorts.
   *
   * Exported from this app's own Figma frame (node 150:2810) as outlined
   * vector — the mark plus the "Dropshiptool" wordmark, no live text, so it
   * renders without needing a font. Do NOT fall back to `/logo.svg`: that is
   * the icon on its own (24x26) and letterboxes to a speck inside this box,
   * while every sibling app shows a full wordmark.
   */
  logo: {
    src: "/maintenance-logo.svg",
    width: 218,
    height: 37.29,
    mobileWidth: 157,
    mobileHeight: 26.86,
  },

  /** Ring left offsets as a percentage of frame width. This app (and Spocket)
   *  place them at 890/834 of 1440 — 109px further right than the other
   *  eight apps, which sit at 781/725. */
  rings: { outer: 61.8, inner: 57.9, outerMobile: 56.3, innerMobile: 41.9 },

  /**
   * Background glow. Unlike AliDrop's, this app's glow is specified as an
   * explicit stop colour + opacity rather than "accent at ~10%": 1728x808 at
   * 82%, opacity 1.0, stop rgba(230,231,255,1) (already ≈ accent #2233EA
   * mixed 10% into white, per the tokens.json derivation rule — just given
   * to us pre-computed). The transparent end-stop is the same colour at
   * alpha 0, per that same rule.
   *
   * mobileRadiusX has NO verified value for this app's own mobile frame — the
   * task spec only gave desktop numbers. Derived here as
   * radiusX * (391/1440), the mobile:desktop frame-width ratio that produces
   * AliDrop's own verified 426 from its 1570 (391/1440 = 0.2715 vs AliDrop's
   * 426/1570 = 0.2713, i.e. the same rule). Flagged as an approximation, not
   * an independently-verified Figma readout.
   */
  glow: {
    radiusX: 1728,
    radiusY: 808,
    atX: 82,
    mobileRadiusX: 469,
    stop: "rgba(230,231,255,1)",
    stopTransparent: "rgba(230,231,255,0)",
  },

  /** Status pill border alpha. Two-digit hex suffix. 24 = 14% (this app),
   *  not the 45 = 27% most other frames use. */
  pillBorderAlpha: "24",

  /**
   * Typography is per-app. This app uses Inter, but with a different weight
   * set (Profile B) than AliDrop's Profile A — do not copy AliDrop's weights.
   */
  font: {
    family:
      '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    headline: 600,
    body: 400,
    cta: 700,
    reassurance: 400,
    eyebrow: 700,
    promoName: 500,
    promoHeadline: 500,
    promoBlurb: 400,
    promoLink: 700,
  },
};
